import { logger } from './logger';
import SecureStorage from './secureStorage';

export interface InterestData {
  summary?: string;
  message?: string;
  vehicle?: {
    make?: string;
    model?: string;
    version?: string;
    trim?: string;
    year?: number | string;
    color?: string;
    vin?: string;
    VIN?: string;
    stock?: string;
    main_picture?: string;
    carfax_url?: string;
    status?: string;
    price?: number;
    mileage?: number | string;
    vdp_url?: string;
    exterior_color?: string;
    interior_color?: string;
    engine?: string;
    transmission?: string;
    fuel_type?: string;
    drivetrain?: string;
    body_style?: string;
    features?: string[];
    description?: string;
  };
  department?: string;
  dealerid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const INTEREST_DATA_KEY = 'chatbot_interest_data';
let isStoringInterestData = false; // Flag to prevent recursive storage calls
let lastStoredData: string | null = null; // Cache to prevent duplicate storage

export const storeInterestData = async (interestData: InterestData): Promise<void> => {
  // Prevent recursive calls
  if (isStoringInterestData) {
    return;
  }
  
  try {
    isStoringInterestData = true;
    
    // Check if data has actually changed to avoid unnecessary storage operations
    const dataString = JSON.stringify(interestData);
    if (dataString === lastStoredData) {
      return; // No changes, skip storage
    }
    
    // Check if we have existing vehicle interest data
    const existingData = await getStoredInterestData();
    
    // Atomic replacement rule: if new vehicle data contains any identifying field (make, model, year),
    // replace the ENTIRE vehicle object to prevent hybrid entries (e.g., "Kia RAM")
    const hasIdentifyingField = interestData?.vehicle && 
      (interestData.vehicle.make || interestData.vehicle.model || interestData.vehicle.year);
    
    const shouldReplaceVehicleData = hasIdentifyingField && existingData?.vehicle;
    
    let dataToStore;
    
    if (shouldReplaceVehicleData) {
      // Replace vehicle data completely when user switches to a new vehicle
      dataToStore = {
        ...existingData,
        ...interestData,
        vehicle: interestData.vehicle, // Complete atomic replacement
        timestamp: new Date().toISOString(),
        chatId: getCurrentChatId()
      };
    } else {
      // Normal merge behavior - merge existing data with new data
      dataToStore = {
        ...existingData,
        ...interestData,
        timestamp: new Date().toISOString(),
        chatId: getCurrentChatId()
      };
    }
    
    await SecureStorage.setItem(INTEREST_DATA_KEY, dataToStore);
    lastStoredData = JSON.stringify(dataToStore); // Update cache
    
    // Dispatch event to notify components of Interest data update (throttled)
    window.dispatchEvent(new CustomEvent('interestDataUpdated', { 
      detail: dataToStore 
    }));
    
  } catch (error) {
    logger.error('Error storing interest data:', error);
  } finally {
    isStoringInterestData = false;
  }
};

export const getStoredInterestData = async (): Promise<InterestData | null> => {
  try {
    logger.log('=== RETRIEVING STORED INTEREST DATA ===');
    const storedData = await SecureStorage.getItem(INTEREST_DATA_KEY);
    logger.log('Raw data from SecureStorage:', storedData);
    
    if (!storedData) {
      logger.log('No stored interest data found');
      return null;
    }
    
    logger.log('Retrieved stored data:', JSON.stringify(storedData, null, 2));
    
    // Return the interest data without the metadata
    const { timestamp, chatId, ...interestData } = storedData;
    return interestData;
  } catch (error) {
    logger.error('Error retrieving interest data:', error);
    return null;
  }
};

export const getVehicleInterest = async (): Promise<InterestData['vehicle'] | null> => {
  try {
    const storedData = await getStoredInterestData();
    return storedData?.vehicle || null;
  } catch (error) {
    logger.error('Error getting vehicle interest:', error);
    return null;
  }
};

export const clearStoredInterestData = (): void => {
  try {
    SecureStorage.removeItem(INTEREST_DATA_KEY);
    logger.log('Interest data cleared from storage');
  } catch (error) {
    logger.error('Error clearing interest data:', error);
  }
};

export const mergeWithStoredInterestData = async (newInterestData?: InterestData): Promise<InterestData | undefined> => {
  logger.log('=== MERGING INTEREST DATA ===');
  logger.log('New interest data:', JSON.stringify(newInterestData, null, 2));
  
  const storedData = await getStoredInterestData();
  logger.log('Stored interest data:', JSON.stringify(storedData, null, 2));
  
  if (!newInterestData && !storedData) {
    logger.log('No new or stored data - returning undefined');
    return undefined;
  }
  
  if (!newInterestData) {
    logger.log('No new data - returning stored data');
    return storedData || undefined;
  }
  
  if (!storedData) {
    logger.log('No stored data - returning new data');
    return newInterestData;
  }
  
  // Merge stored data with new data, with new data taking precedence
  // IMPORTANT: If new data has a department, it should override stored department
  // SECURITY: Contact data is no longer stored in localStorage
  const merged: InterestData = {
    summary: newInterestData.summary || storedData.summary,
    message: newInterestData.message || storedData.message,
    vehicle: {
      ...storedData.vehicle,
      ...newInterestData.vehicle
    },
    // NEW: Give priority to new department over stored department
    department: newInterestData.department || storedData.department,
    dealerid: newInterestData.dealerid || storedData.dealerid,
    utm_source: newInterestData.utm_source || storedData.utm_source,
    utm_campaign: newInterestData.utm_campaign || storedData.utm_campaign
  };
  
  logger.log('=== MERGE RESULT ===');
  logger.log('Merged interest data:', JSON.stringify(merged, null, 2));
  return merged;
};

// Helper function to get current chat ID from session storage or generate a stable one
const getCurrentChatId = (): string => {
  // Try to get existing chat ID from sessionStorage first
  const existingChatId = sessionStorage.getItem('chatbot_chat_id');
  if (existingChatId) {
    return existingChatId;
  }
  
  // If no existing ID, generate one and store it for the session
  const newChatId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  sessionStorage.setItem('chatbot_chat_id', newChatId);
  return newChatId;
};
